import {
  isRouteErrorResponse,
  Link,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from "react-router";
import { SITE_NAME, SITE_TAGLINE } from "@veo/types";
import { SiteFooter, SiteHeader } from "./components/SiteChrome";
import { FALLBACK_OG_IMAGE } from "./lib/seo";
import {
  absoluteUrl,
  loadSiteContext,
  settingString,
  SITE_URL,
  useSite,
  type SiteSettings,
} from "./lib/site";
import type { Route } from "./+types/root";
import "./app.css";

/**
 * Site chrome is loaded once, here, rather than in every route.
 *
 * React Router revalidates root loader data only when a mutation warrants it,
 * so navigation and settings are fetched on the first request and reused across
 * client-side navigation — the header does not re-request itself per page.
 */
export async function loader() {
  return { site: await loadSiteContext() };
}

export const meta: Route.MetaFunction = ({ loaderData }) => {
  // React Router v8 names this `loaderData` in meta args (it was `data` in v7).
  const settings = loaderData?.site.settings ?? {};
  const name = settingString(settings, "site.name", SITE_NAME);
  const tagline = settingString(settings, "site.tagline", SITE_TAGLINE);

  /**
   * Only the tags a route is expected to replace.
   *
   * React Router does not merge a parent's meta into a child's — the deepest
   * route that exports `meta` supplies the whole set. Anything site-wide put
   * here is therefore silently dropped on every page that has its own meta,
   * which is all of them. `og:site_name`, `og:locale` and the organisation
   * markup used to be here and reached no page at all; they are emitted from
   * `SiteMeta` in the document head instead.
   */
  return [
    { title: settingString(settings, "seo.defaultMetaTitle", `${name} — ${tagline}`) },
    { name: "description", content: defaultDescription(settings) },
  ];
};

function defaultDescription(settings: SiteSettings): string {
  return settingString(
    settings,
    "seo.defaultMetaDescription",
    settingString(
      settings,
      "site.description",
      "Independent investment advisory for individuals and families in Kenya.",
    ),
  );
}

/**
 * The tags that are true of every page, rendered into the head directly.
 *
 * Rendering rather than returning descriptors is the whole point: these survive
 * a route defining its own `meta`, which the root's meta does not.
 *
 * `useRouteLoaderData` rather than a prop because `Layout` also renders the
 * error boundary, where the root loader may never have run — `useSite` already
 * returns empty settings in that case, and every value below has a fallback.
 */
function SiteMeta() {
  const { settings } = useSite();
  const name = settingString(settings, "site.name", SITE_NAME);
  const description = defaultDescription(settings);
  const logo = settingString(settings, "site.logoUrl") || absoluteUrl("/logo-veo.png");
  /*
    `logo` is the mark itself; `image` is a representative picture of the
    organisation, which wants the landscape share card rather than a second
    copy of the logo.
  */
  const image =
    settingString(settings, "seo.defaultOgImageUrl") || absoluteUrl(FALLBACK_OG_IMAGE);
  const socials = [
    settingString(settings, "social.linkedin"),
    settingString(settings, "social.facebook"),
    settingString(settings, "social.twitter"),
    settingString(settings, "social.instagram"),
    settingString(settings, "social.youtube"),
  ].filter(Boolean);

  /**
   * Organisation markup, emitted once for the whole site. Search engines use it
   * for the knowledge panel; a route-level `Service` or `Article` block sits
   * alongside it rather than replacing it.
   *
   * `WebSite` carries the search box: `/insights` takes a `q` parameter, and
   * declaring it is what lets a result for this site offer a search field of
   * its own rather than only the four blue links.
   */
  const graph = [
    {
      "@type": "FinancialService",
      "@id": `${SITE_URL}/#organisation`,
      name,
      description,
      url: SITE_URL,
      logo,
      image,
      areaServed: { "@type": "Country", name: "Kenya" },
      ...(socials.length > 0 ? { sameAs: socials } : {}),
      ...(settingString(settings, "contact.email")
        ? { email: settingString(settings, "contact.email").trim() }
        : {}),
      ...(settingString(settings, "contact.phone")
        ? { telephone: settingString(settings, "contact.phone") }
        : {}),
      address: {
        "@type": "PostalAddress",
        ...(settingString(settings, "contact.addressLine1")
          ? { streetAddress: settingString(settings, "contact.addressLine1") }
          : {}),
        addressLocality: settingString(settings, "contact.city", "Nairobi"),
        addressCountry: settingString(settings, "contact.country", "Kenya"),
      },
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      name,
      url: SITE_URL,
      publisher: { "@id": `${SITE_URL}/#organisation` },
      inLanguage: "en-KE",
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${SITE_URL}/insights?q={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
      },
    },
  ];

  return (
    <>
      <meta property="og:site_name" content={name} />
      <meta property="og:locale" content="en_KE" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          // Every value here is editor-supplied, so a `</script>` in a settings
          // field would otherwise end the block early and spill the rest into
          // the page. Escaping the angle bracket is what the other JSON-LD
          // blocks do, for the same reason.
          __html: JSON.stringify({ "@context": "https://schema.org", "@graph": graph }).replace(
            /</g,
            "\\u003c",
          ),
        }}
      />
    </>
  );
}

export const links: Route.LinksFunction = () => [
  /*
   * Several sizes rather than one: a browser picks the closest and scales,
   * and the mark has enough detail that a 16px downscale of a 48px file is
   * visibly muddier than a file drawn at 16px. Built by
   * scripts/build-logo-assets.mjs from the supplied artwork.
   */
  { rel: "icon", href: "/favicon-32.png", type: "image/png", sizes: "32x32" },
  { rel: "icon", href: "/favicon-16.png", type: "image/png", sizes: "16x16" },
  { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
];

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en-KE">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#0B1F3A" />
        <Meta />
        <Links />
        <SiteMeta />
      </head>
      <body className="min-h-dvh bg-surface-50 textured text-ink-900 antialiased">
        {/* First focusable element on the page — keyboard users should never
            have to tab through the whole navigation to reach content. */}
        <a
          href="#main"
          className="sr-only-focusable absolute left-4 top-4 z-50 rounded-md bg-navy-900 px-4 py-2 text-sm font-semibold text-cream-100"
        >
          Skip to main content
        </a>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return (
    <>
      <SiteHeader />
      <Outlet />
      <SiteFooter />
    </>
  );
}

/**
 * The 404 and error pages.
 *
 * Chrome is rendered here too, so a visitor who lands on a dead link still has
 * navigation to recover with. `useSite()` degrades to empty menus when the root
 * loader is what failed — which is exactly the moment a component that assumed
 * data would crash the error page as well.
 */
export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  const status = isRouteErrorResponse(error) ? error.status : 500;

  return (
    <>
      <SiteHeader />
      <main id="main" className="container-site section-y">
        <div className="container-content text-center">
          <p className="eyebrow">Error {status}</p>
          <h1 className="mt-4 text-5xl">{notFound ? "Page not found" : "We hit a problem"}</h1>
          <p className="mt-4 text-lg text-ink-500">
            {notFound
              ? "The page you are looking for does not exist or has been moved. The links below cover most of what people come here for."
              : "Something went wrong on our side. Please try again in a moment — if it keeps happening, do get in touch."}
          </p>

          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="rounded-md bg-navy-900 px-6 py-3 text-sm font-semibold text-cream-100 transition-colors hover:bg-navy-800"
            >
              Return to homepage
            </Link>
            <Link
              to="/contact"
              className="rounded-md px-6 py-3 text-sm font-semibold text-navy-800 ring-1 ring-inset ring-ink-300 transition-colors hover:bg-cream-200"
            >
              Contact us
            </Link>
          </div>

          {notFound && (
            <ul className="mx-auto mt-12 grid max-w-lg gap-3 text-left sm:grid-cols-2">
              {[
                { to: "/services", label: "Our services" },
                { to: "/insights", label: "Insights" },
                { to: "/about", label: "About VEO" },
                { to: "/book-consultation", label: "Book a consultation" },
              ].map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="block rounded-lg border border-ink-200 px-5 py-4 text-sm font-medium text-navy-800 transition-colors hover:border-gold-500 hover:bg-cream-100"
                  >
                    {item.label}
                    <span aria-hidden className="ml-1">
                      →
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}

          {/* Locally the stack is the whole point of an error page; it must
              never reach a visitor in production. */}
          {import.meta.env.DEV && !isRouteErrorResponse(error) && error instanceof Error && (
            <pre className="mt-10 overflow-x-auto rounded-lg bg-ink-900 p-5 text-left text-xs text-cream-100">
              {error.stack}
            </pre>
          )}
        </div>
      </main>
      <SiteFooter />
    </>
  );
}
